import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Building2, Save, Phone, MapPin, Users, Briefcase, Shield, Globe, Award, Clock, CheckCircle, Sparkles, FileText, TrendingUp, Hash, Building } from 'lucide-react';
import ImageUploader from './ImageUploader';
import PhoneInput from '../ui/PhoneInput';
import EmailInput from '../ui/EmailInput';
import SiretInput from '../ui/SiretInput';
import { DatePickerFr } from '../ui/DatePickerFr';
import CountrySelect from '../ui/CountrySelect';
import ChangePasswordForm from './ChangePasswordForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CooperProfileEdit({ cooper, onUpdate, getAuthHeader }) {
  const [formData, setFormData] = useState({
    logo_url: cooper?.logo_url || '',
    email: cooper?.email || '',
    telephone: cooper?.telephone || '',
    nom_entreprise: cooper?.nom_entreprise || cooper?.entreprise || '',
    siret: cooper?.siret || '',
    adresse: cooper?.adresse || '',
    ville: cooper?.ville || '',
    code_postal: cooper?.code_postal || '',
    pays: cooper?.pays || 'France',
    secteur_activite: cooper?.secteur_activite || '',
    effectif: cooper?.effectif || '',
    statut_juridique: cooper?.statut_juridique || '',
    code_naf: cooper?.code_naf || '',
    site_web: cooper?.site_web || '',
    description: cooper?.description || '',
    date_creation: cooper?.date_creation || '',
    capital_social: cooper?.capital_social || ''
  });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('general');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUploaded = (url) => {
    setFormData(prev => ({ ...prev, logo_url: url }));
    toast.success('Logo mis à jour !');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSend = Object.fromEntries(
        Object.entries(formData).filter(([_, v]) => v !== '' && v !== null)
      );

      const response = await axios.put(
        `${BACKEND_URL}/api/users/me/profile`,
        dataToSend,
        { headers: getAuthHeader() }
      );

      toast.success('Profil mis à jour avec succès !');
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Calcul du pourcentage de complétion du profil
  const calculateCompletion = () => {
    const fields = ['nom_entreprise', 'email', 'telephone', 'siret', 'adresse', 'secteur_activite', 'effectif', 'statut_juridique'];
    const filled = fields.filter(f => formData[f] && formData[f].toString().trim() !== '').length;
    return Math.round((filled / fields.length) * 100);
  };

  const completion = calculateCompletion();

  return (
    <div className="space-y-6">
      {/* En-tête avec référence ESS et statistiques */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Carte Référence ESS */}
        <Card className="lg:col-span-2 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 text-white border-0 shadow-xl overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          <CardContent className="pt-6 relative z-10">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-5 w-5 text-emerald-200" />
                  <span className="text-sm font-medium text-emerald-100">Partenaire COOPER'S Certifié</span>
                </div>
                {cooper?.ref_ess ? (
                  <>
                    <p className="text-xs text-emerald-200 mb-1">Votre référence ESS</p>
                    <p className="text-4xl font-bold font-mono tracking-wider mb-2">{cooper.ref_ess}</p>
                  </>
                ) : (
                  <p className="text-2xl font-bold mb-2">{formData.nom_entreprise || 'Mon Entreprise'}</p>
                )}
                <div className="flex items-center gap-3 mt-4">
                  {cooper?.statut === 'actif' && (
                    <Badge className="bg-slate-800 text-white border-slate-600 hover:bg-slate-700 font-semibold">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Compte actif
                    </Badge>
                  )}
                  {cooper?.date_adhesion && (
                    <Badge className="bg-slate-800 text-white border-slate-600 font-semibold">
                      <Clock className="h-3 w-3 mr-1" />
                      Membre depuis {new Date(cooper.date_adhesion).getFullYear()}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                <Building2 className="h-12 w-12" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Carte Complétion Profil */}
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0 shadow-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-slate-400">Profil complété</p>
                <p className="text-3xl font-bold">{completion}%</p>
              </div>
              <div className={`p-3 rounded-xl ${completion === 100 ? 'bg-emerald-500/20' : 'bg-blue-500/20'}`}>
                {completion === 100 ? (
                  <Award className="h-8 w-8 text-emerald-400" />
                ) : (
                  <TrendingUp className="h-8 w-8 text-blue-400" />
                )}
              </div>
            </div>
            <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-700 ${
                  completion === 100 
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-400' 
                    : 'bg-gradient-to-r from-blue-500 to-purple-500'
                }`}
                style={{ width: `${completion}%` }}
              />
            </div>
            {completion < 100 && (
              <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-yellow-400" />
                Complétez votre profil pour plus de visibilité
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Navigation par sections */}
      <div className="flex flex-wrap gap-2 p-1 bg-slate-100 rounded-xl">
        {[
          { id: 'general', label: 'Informations générales', icon: Building2 },
          { id: 'contact', label: 'Coordonnées', icon: Phone },
          { id: 'details', label: 'Détails entreprise', icon: FileText },
          { id: 'security', label: 'Sécurité', icon: Shield }
        ].map(section => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeSection === section.id
                ? 'bg-white text-blue-600 shadow-md'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
          >
            <section.icon className="h-4 w-4" />
            <span className="hidden sm:inline">{section.label}</span>
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section: Informations générales */}
        {activeSection === 'general' && (
          <>
            {/* Logo */}
            <Card className="border-2 border-slate-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  Logo de l'entreprise
                </CardTitle>
                <CardDescription>
                  Votre logo sera affiché sur vos documents et communications
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <ImageUploader
                  currentImageUrl={formData.logo_url}
                  onImageUploaded={handleImageUploaded}
                  type="logo"
                  getAuthHeader={getAuthHeader}
                />
              </CardContent>
            </Card>

            {/* Informations entreprise */}
            <Card className="border-2 border-slate-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Building className="h-5 w-5 text-emerald-600" />
                  </div>
                  Informations de l'entreprise
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                      <Building2 className="h-4 w-4 text-slate-500" />
                      Nom de l'entreprise *
                    </label>
                    <input
                      type="text"
                      name="nom_entreprise"
                      value={formData.nom_entreprise}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-lg font-medium"
                      required
                      placeholder="Raison sociale de votre entreprise"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                      <Hash className="h-4 w-4 text-slate-500" />
                      SIRET *
                    </label>
                    <SiretInput
                      value={formData.siret}
                      onChange={(val) => setFormData(prev => ({ ...prev, siret: val }))}
                      placeholder="14 chiffres"
                      required
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                      <FileText className="h-4 w-4 text-slate-500" />
                      Code NAF
                    </label>
                    <input
                      type="text"
                      name="code_naf"
                      value={formData.code_naf}
                      onChange={handleChange}
                      placeholder="Ex: 7022Z"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                      <Briefcase className="h-4 w-4 text-slate-500" />
                      Secteur d'activité
                    </label>
                    <input
                      type="text"
                      name="secteur_activite"
                      value={formData.secteur_activite}
                      onChange={handleChange}
                      placeholder="Ex: BTP, Services, Conseil..."
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                      <FileText className="h-4 w-4 text-slate-500" />
                      Statut juridique
                    </label>
                    <select
                      name="statut_juridique"
                      value={formData.statut_juridique}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                    >
                      <option value="" disabled>-- Sélectionner --</option>
                      <option value="SARL">SARL</option>
                      <option value="SA">SA</option>
                      <option value="SAS">SAS</option>
                      <option value="EURL">EURL</option>
                      <option value="SASU">SASU</option>
                      <option value="EI">Entreprise Individuelle</option>
                      <option value="Micro-entreprise">Micro-entreprise</option>
                      <option value="SCOP">SCOP</option>
                      <option value="SCIC">SCIC</option>
                      <option value="Association">Association</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Section: Coordonnées */}
        {activeSection === 'contact' && (
          <Card className="border-2 border-slate-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Phone className="h-5 w-5 text-purple-600" />
                </div>
                Coordonnées
              </CardTitle>
              <CardDescription>
                Vos informations de contact professionnelles
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <EmailInput
                    value={formData.email}
                    onChange={(val) => setFormData(prev => ({ ...prev, email: val }))}
                    placeholder="contact@entreprise.fr"
                    required
                    label="Email professionnel"
                  />
                </div>

                <div>
                  <PhoneInput
                    value={formData.telephone}
                    onChange={(val) => setFormData(prev => ({ ...prev, telephone: val }))}
                    placeholder="Numéro de téléphone"
                    required
                    label="Téléphone"
                    showLabel={true}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <MapPin className="h-4 w-4 text-slate-500" />
                    Adresse complète
                  </label>
                  <input
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    placeholder="Numéro et nom de rue"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <MapPin className="h-4 w-4 text-slate-500" />
                    Code postal
                  </label>
                  <input
                    type="text"
                    name="code_postal"
                    value={formData.code_postal}
                    onChange={handleChange}
                    placeholder="Ex: 75001"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Building className="h-4 w-4 text-slate-500" />
                    Ville
                  </label>
                  <input
                    type="text"
                    name="ville"
                    value={formData.ville}
                    onChange={handleChange}
                    placeholder="Ex: Paris"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <CountrySelect
                    value={formData.pays}
                    onChange={(val) => setFormData(prev => ({ ...prev, pays: val }))}
                    label="Pays"
                    showLabel={true}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Globe className="h-4 w-4 text-slate-500" />
                    Site web
                  </label>
                  <input
                    type="url"
                    name="site_web"
                    value={formData.site_web}
                    onChange={handleChange}
                    placeholder="https://www.votre-entreprise.fr"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section: Détails entreprise */}
        {activeSection === 'details' && (
          <Card className="border-2 border-slate-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FileText className="h-5 w-5 text-amber-600" />
                </div>
                Détails supplémentaires
              </CardTitle>
              <CardDescription>
                Informations complémentaires sur votre entreprise
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Users className="h-4 w-4 text-slate-500" />
                    Effectif
                  </label>
                  <input
                    type="number"
                    name="effectif"
                    value={formData.effectif}
                    onChange={handleChange}
                    min="1"
                    placeholder="Nombre de salariés"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Clock className="h-4 w-4 text-slate-500" />
                    Date de création
                  </label>
                  <DatePickerFr
                    value={formData.date_creation}
                    onChange={(v) => handleChange({ target: { name: 'date_creation', value: v } })}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <TrendingUp className="h-4 w-4 text-slate-500" />
                    Capital social (€)
                  </label>
                  <input
                    type="number"
                    name="capital_social"
                    value={formData.capital_social}
                    onChange={handleChange}
                    min="0"
                    placeholder="Ex: 10000"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <FileText className="h-4 w-4 text-slate-500" />
                    Description de l'entreprise
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Décrivez brièvement votre entreprise, vos activités principales et vos valeurs..."
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section: Sécurité */}
        {activeSection === 'security' && (
          <ChangePasswordForm 
            getAuthHeader={getAuthHeader}
            onSuccess={() => toast.success('Mot de passe modifié avec succès !')}
          />
        )}

        {/* Bouton de sauvegarde (sauf pour sécurité) */}
        {activeSection !== 'security' && (
          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              disabled={saving}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all text-base font-semibold"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Enregistrer les modifications
                </>
              )}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
